import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  UserIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  HeartIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import FileUpload from "../components/ui/FileUpload";
import { showError, showSuccess } from "../utils/toast";

const ProfilePage = () => {
  const { user, apiCall } = useAuth();

  const [activeTab, setActiveTab] = useState("personal");
  const [isEditing, setIsEditing] = useState(false);
  const [pets, setPets] = useState([]);
  const [savingPetId, setSavingPetId] = useState(null);
  const [selectedPetId, setSelectedPetId] = useState(null);
  const [editingPetId, setEditingPetId] = useState(null);

  const [profileData, setProfileData] = useState({
    firstName: "",
    lastName: "",
    gender: "",
    dateOfBirth: "",
    email: "",
    phone: "",
    address: "",
    country: "",
    state: "",
    city: "",
    timezone: "",
    emergencyName: "",
    emergencyRelation: "",
    emergencyPhone: "",
    vetName: "",
    vetClinic: "",
    vetPhone: "",
    notificationMethods: ["email"],
    marketingOptIn: false,
    avatarUrl: null,
  });

  useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user.firstName || user.first_name || "",
        lastName: user.lastName || user.last_name || "",
        gender: user.gender || "",
        dateOfBirth: user.dateOfBirth || user.date_of_birth || "",
        email: user.email || "",
        phone: user.phone || "",
        address: user.address || "",
        country: user.country || "",
        state: user.state || "",
        city: user.city || "",
        timezone: user.timezone || "",
        emergencyName:
          user.emergencyContactName || user.emergency_contact_name || "",
        emergencyRelation:
          user.emergencyContactRelation ||
          user.emergency_contact_relation ||
          "",
        emergencyPhone:
          user.emergencyContactPhone || user.emergency_contact_phone || "",
        vetName: user.vetName || user.vet_name || "",
        vetClinic: user.vetClinic || user.vet_clinic || "",
        vetPhone: user.vetPhone || user.vet_phone || "",
        notificationMethods:
          user.notificationMethods || user.notification_methods || ["email"],
        marketingOptIn: user.marketingOptIn || user.marketing_opt_in || false,
        avatarUrl: user.avatarURL || user.avatar_url || null,
      });

      loadUserPets();
    }
  }, [user]);

  const tabs = [
    { id: "personal", name: "Personal Data", icon: UserIcon },
    { id: "contact", name: "Contacts", icon: UserIcon },
    { id: "emergency", name: "Emergency Contacts", icon: UserIcon },
    { id: "preferences", name: "Preferences", icon: UserIcon },
    { id: "pets", name: "Pets", icon: UserIcon },
  ];

  const fieldClass =
    "w-full bg-[#F8F9FB] border border-gray-100 rounded-[18px] px-4 py-3 text-sm font-bold text-[#0A1128] outline-none transition-all placeholder:text-slate-300 focus:bg-white focus:border-orange-100 focus:ring-4 focus:ring-orange-50 disabled:bg-zinc-50 disabled:text-slate-400 disabled:cursor-not-allowed";

  const labelClass =
    "block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2";

  const sectionTitleClass =
    "text-[12px] font-black uppercase tracking-widest text-[#0A1128] italic";

  const getAuthToken = async () => {
    if (typeof user?.getIdToken === "function") {
      return await user.getIdToken();
    }

    return user?.token || user?.accessToken || "";
  };

  const normalizePet = (pet) => {
    const rawDate = pet.dateOfBirth || pet.date_of_birth || "";

    return {
      ...pet,
      id: pet.id,
      name: pet.name || "",
      species: pet.species || pet.pet_type || pet.pet_type_name || "",
      breed: pet.breed || pet.breed_name || "",
      gender: pet.gender || "",
      dateOfBirth: rawDate ? String(rawDate).slice(0, 10) : "",
      weight: pet.weight || 0,
      microchipId: pet.microchipId || pet.microchip_id || "",
      sterilized: Boolean(pet.sterilized),
      photoUrl: pet.photoUrl || pet.photo_url || "",
      vaccinations: Array.isArray(pet.vaccinations) ? pet.vaccinations : [],
      allergies: Array.isArray(pet.allergies) ? pet.allergies : [],
      medications: pet.medications || "",
      specialNeeds: pet.specialNeeds || pet.special_needs || "",
      vetContact: pet.vetContact || pet.vet_contact || "",
      notes: pet.notes || "",
      isNew: Boolean(pet.isNew),
    };
  };

  const toBackendDateTime = (dateValue) => {
    if (!dateValue) {
      return null;
    }

    const date = new Date(`${dateValue}T00:00:00`);

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return date.toISOString();
  };

  const loadUserPets = async () => {
    if (!user || !apiCall) return;

    try {
      const result = await apiCall("/pets");
      const loadedPets = result.data || result.pets || [];

      setPets(Array.isArray(loadedPets) ? loadedPets.map(normalizePet) : []);
    } catch (error) {
      console.error("Failed to load pets:", error);
      setPets([]);
    }
  };

  const handleInputChange = (field, value) => {
    setProfileData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAvatarUpload = async (files) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    const formData = new FormData();
    formData.append("file", file.file);

    try {
      const token = await getAuthToken();

      const response = await fetch("/api/v1/uploads/avatar", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to upload avatar");
      }

      const result = await response.json();

      handleInputChange("avatarUrl", result.file?.url || result.url || "");

      showSuccess("Фото профиля успешно загружено", "Готово");
    } catch (error) {
      console.error("Error uploading avatar:", error);
      showError("Не удалось загрузить фото. Попробуйте ещё раз.", "Ошибка");
    }
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return 0;

    const today = new Date();
    const birthDate = new Date(dateOfBirth);

    if (Number.isNaN(birthDate.getTime())) return 0;

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  };

  const addPet = () => {
    const newPet = {
      id: `new-${Date.now()}`,
      name: "",
      species: "",
      breed: "",
      gender: "",
      dateOfBirth: "",
      weight: 0,
      microchipId: "",
      sterilized: false,
      photoUrl: "",
      vaccinations: [],
      allergies: [],
      medications: "",
      specialNeeds: "",
      vetContact: "",
      notes: "",
      isNew: true,
    };

    setPets((prev) => [...prev, newPet]);
    setActiveTab("pets");
    setSelectedPetId(newPet.id);
    setEditingPetId(newPet.id);
  };

  const updatePet = (petId, updates) => {
    setPets((prev) =>
      prev.map((pet) => (pet.id === petId ? { ...pet, ...updates } : pet)),
    );
  };

  const savePet = async (pet) => {
    if (!pet.name?.trim()) {
      showError("Введите имя питомца", "Ошибка");
      return false;
    }

    try {
      setSavingPetId(pet.id);

      const payload = {
        name: pet.name?.trim(),
        species: pet.species || "",
        breed: pet.breed || "",
        gender: pet.gender || "unknown",
        date_of_birth: toBackendDateTime(pet.dateOfBirth),
        weight: Number(pet.weight || 0),
        microchip_id: pet.microchipId || "",
        sterilized: Boolean(pet.sterilized),
        photo_url: pet.photoUrl || "",
        allergies: Array.isArray(pet.allergies) ? pet.allergies : [],
        medications: pet.medications || "",
        special_needs: pet.specialNeeds || "",
        vet_contact: pet.vetContact || "",
        notes: pet.notes || "",
      };

      const isNewPet = pet.isNew || String(pet.id).startsWith("new-");

      const result = await apiCall(isNewPet ? "/pets" : `/pets/${pet.id}`, {
        method: isNewPet ? "POST" : "PUT",
        body: JSON.stringify(payload),
      });

      if (!result?.success && !result?.data && !result?.pet) {
        throw new Error(result?.error || "Failed to save pet");
      }

      await loadUserPets();

      showSuccess("Питомец успешно сохранён", "Сохранено");

      return true;
    } catch (error) {
      console.error("Save pet error:", error);
      showError(error.message || "Не удалось сохранить питомца", "Ошибка");
      return false;
    } finally {
      setSavingPetId(null);
    }
  };

  const deletePet = async (petId) => {
    const isNewPet = String(petId).startsWith("new-");

    if (isNewPet) {
      setPets((prev) => prev.filter((pet) => pet.id !== petId));

      if (String(selectedPetId) === String(petId)) {
        setSelectedPetId(null);
        setEditingPetId(null);
      }

      showSuccess("Питомец удалён", "Удалено");
      return true;
    }

    try {
      await apiCall(`/pets/${petId}`, {
        method: "DELETE",
      });

      setPets((prev) => prev.filter((pet) => pet.id !== petId));

      if (String(selectedPetId) === String(petId)) {
        setSelectedPetId(null);
        setEditingPetId(null);
      }

      showSuccess("Питомец успешно удалён", "Удалено");

      return true;
    } catch (error) {
      console.error("Delete pet error:", error);

      if (
        error.status === 409 ||
        error.message?.includes("409") ||
        error.message?.includes("Conflict") ||
        error.message?.includes("Pet has bookings") ||
        error.message?.includes("bookings") ||
        error.message?.includes("cannot be deleted")
      ) {
        showError(
          "Этот питомец уже записан на услугу или есть в истории бронирований, поэтому его нельзя удалить.",
          "Удаление невозможно",
        );

        return false;
      }

      showError(error.message || "Не удалось удалить питомца", "Ошибка");

      return false;
    }
  };

  const handlePetPhotoUpload = async (petId, files) => {
    if (!files || files.length === 0) return;

    if (String(petId).startsWith("new-")) {
      showError(
        "Сначала сохраните питомца, потом загрузите фото.",
        "Фото недоступно",
      );
      return;
    }

    const file = files[0];
    const formData = new FormData();
    formData.append("file", file.file);

    try {
      const token = await getAuthToken();

      const response = await fetch(`/api/v1/pets/${petId}/upload-photo`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to upload pet photo");
      }

      const result = await response.json();

      updatePet(petId, {
        photoUrl: result.file?.url || result.url || result.photo_url || "",
      });

      showSuccess("Фото питомца успешно загружено", "Готово");
    } catch (error) {
      console.error("Error uploading pet photo:", error);
      showError(
        "Не удалось загрузить фото питомца. Попробуйте ещё раз.",
        "Ошибка",
      );
    }
  };

  const renderPersonalTab = () => (
    <div className="space-y-8">
      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
        <div className="bg-[#F8F9FB] rounded-[32px] border border-gray-50 p-6 h-fit">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4">
            Profile Photo
          </p>

          <div className="w-28 h-28 bg-white rounded-[28px] flex items-center justify-center overflow-hidden shadow-sm border border-gray-50 mb-5">
            {profileData.avatarUrl ? (
              <img
                src={profileData.avatarUrl}
                alt="Avatar"
                className="w-full h-full object-cover"
              />
            ) : (
              <UserIcon className="w-12 h-12 text-slate-300" />
            )}
          </div>

          {isEditing && (
            <FileUpload
              onFilesSelect={handleAvatarUpload}
              maxFiles={1}
              maxFileSize={5 * 1024 * 1024}
              acceptedTypes={["image/jpeg", "image/png", "image/webp"]}
              allowMultiple={false}
              showPreview={false}
              className="max-w-md"
            >
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-[#0A1128]">
                  Upload photo
                </p>
                <p className="text-[9px] font-bold text-slate-400 mt-1 uppercase">
                  JPG, PNG or WebP up to 5MB
                </p>
              </div>
            </FileUpload>
          )}
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className={labelClass}>First Name</label>
              <input
                type="text"
                value={profileData.firstName}
                onChange={(e) => handleInputChange("firstName", e.target.value)}
                disabled={!isEditing}
                className={fieldClass}
              />
            </div>

            <div>
              <label className={labelClass}>Last Name</label>
              <input
                type="text"
                value={profileData.lastName}
                onChange={(e) => handleInputChange("lastName", e.target.value)}
                disabled={!isEditing}
                className={fieldClass}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className={labelClass}>Gender</label>
              <select
                value={profileData.gender}
                onChange={(e) => handleInputChange("gender", e.target.value)}
                disabled={!isEditing}
                className={fieldClass}
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className={labelClass}>
                Date of Birth / Age: {calculateAge(profileData.dateOfBirth)}
              </label>
              <input
                type="date"
                value={profileData.dateOfBirth}
                onChange={(e) =>
                  handleInputChange("dateOfBirth", e.target.value)
                }
                disabled={!isEditing}
                className={fieldClass}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContactTab = () => (
    <div className="space-y-6">
      <div>
        <label className={labelClass}>Email</label>
        <input
          type="email"
          value={profileData.email}
          onChange={(e) => handleInputChange("email", e.target.value)}
          disabled={!isEditing}
          className={fieldClass}
        />
      </div>

      <div>
        <label className={labelClass}>Phone</label>
        <PhoneInput
          country={"us"}
          value={profileData.phone}
          onChange={(phone) => handleInputChange("phone", phone)}
          disabled={!isEditing}
          inputClass="!w-full !h-auto !bg-[#F8F9FB] !border !border-gray-100 !rounded-[18px] !pl-14 !pr-4 !py-3 !text-sm !font-bold !text-[#0A1128] !outline-none focus:!border-orange-100 focus:!ring-4 focus:!ring-orange-50 disabled:!bg-zinc-50"
          buttonClass="!border-gray-100 !bg-white !rounded-l-[18px]"
          containerClass="w-full"
        />
      </div>

      <div>
        <label className={labelClass}>Address</label>
        <textarea
          value={profileData.address}
          onChange={(e) => handleInputChange("address", e.target.value)}
          disabled={!isEditing}
          rows={3}
          className={fieldClass}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div>
          <label className={labelClass}>Country</label>
          <select
            value={profileData.country}
            onChange={(e) => handleInputChange("country", e.target.value)}
            disabled={!isEditing}
            className={fieldClass}
          >
            <option value="">Select country</option>
            <option value="United States">United States</option>
            <option value="Turkmenistan">Turkmenistan</option>
            <option value="Ukraine">Ukraine</option>
            <option value="Belarus">Belarus</option>
            <option value="Kazakhstan">Kazakhstan</option>
          </select>
        </div>

        <div>
          <label className={labelClass}>Region</label>
          <input
            type="text"
            value={profileData.state}
            onChange={(e) => handleInputChange("state", e.target.value)}
            disabled={!isEditing}
            className={fieldClass}
          />
        </div>

        <div>
          <label className={labelClass}>City</label>
          <input
            type="text"
            value={profileData.city}
            onChange={(e) => handleInputChange("city", e.target.value)}
            disabled={!isEditing}
            className={fieldClass}
          />
        </div>
      </div>

      <div>
        <label className={labelClass}>Timezone</label>
        <select
          value={profileData.timezone}
          onChange={(e) => handleInputChange("timezone", e.target.value)}
          disabled={!isEditing}
          className={fieldClass}
        >
          <option value="">Select timezone</option>
          <option value="Asia/Ashgabat">Ashgabat (GMT+5)</option>
          <option value="America/Los_Angeles">Los Angeles (GMT-7)</option>
          <option value="Europe/Kiev">Kiev (GMT+2)</option>
          <option value="Asia/Yekaterinburg">Yekaterinburg (GMT+5)</option>
          <option value="Asia/Novosibirsk">Novosibirsk (GMT+7)</option>
        </select>
      </div>
    </div>
  );

  const renderEmergencyTab = () => (
    <div className="space-y-8">
      <div className="bg-orange-50 border border-orange-100 rounded-[28px] p-6">
        <h3 className="text-[12px] font-black uppercase tracking-widest text-[#FF5C35] italic mb-2">
          Emergency Contact
        </h3>
        <p className="text-[11px] font-bold text-orange-500 uppercase tracking-wide">
          Specify a contact person to whom you can turn in an emergency
          situation
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className={labelClass}>Name and Relation</label>
          <input
            type="text"
            value={profileData.emergencyName}
            onChange={(e) => handleInputChange("emergencyName", e.target.value)}
            disabled={!isEditing}
            className={fieldClass}
            placeholder="John Johnson (husband)"
          />
        </div>

        <div>
          <label className={labelClass}>Emergency Contact Phone</label>
          <PhoneInput
            country={"us"}
            value={profileData.emergencyPhone}
            onChange={(phone) => handleInputChange("emergencyPhone", phone)}
            disabled={!isEditing}
            inputClass="!w-full !h-auto !bg-[#F8F9FB] !border !border-gray-100 !rounded-[18px] !pl-14 !pr-4 !py-3 !text-sm !font-bold !text-[#0A1128] !outline-none focus:!border-orange-100 focus:!ring-4 focus:!ring-orange-50 disabled:!bg-zinc-50"
            buttonClass="!border-gray-100 !bg-white !rounded-l-[18px]"
            containerClass="w-full"
          />
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-100 rounded-[28px] p-6">
        <h3 className="text-[12px] font-black uppercase tracking-widest text-blue-500 italic mb-2">
          Veterinary Contact
        </h3>
        <p className="text-[11px] font-bold text-blue-400 uppercase tracking-wide">
          Contact details of your main veterinarian or clinic
        </p>
      </div>

      <div className="space-y-5">
        <div>
          <label className={labelClass}>Veterinarian Name</label>
          <input
            type="text"
            value={profileData.vetName}
            onChange={(e) => handleInputChange("vetName", e.target.value)}
            disabled={!isEditing}
            className={fieldClass}
          />
        </div>

        <div>
          <label className={labelClass}>Clinic Name</label>
          <input
            type="text"
            value={profileData.vetClinic}
            onChange={(e) => handleInputChange("vetClinic", e.target.value)}
            disabled={!isEditing}
            className={fieldClass}
          />
        </div>

        <div>
          <label className={labelClass}>Veterinary Clinic Phone</label>
          <PhoneInput
            country={"us"}
            value={profileData.vetPhone}
            onChange={(phone) => handleInputChange("vetPhone", phone)}
            disabled={!isEditing}
            inputClass="!w-full !h-auto !bg-[#F8F9FB] !border !border-gray-100 !rounded-[18px] !pl-14 !pr-4 !py-3 !text-sm !font-bold !text-[#0A1128] !outline-none focus:!border-orange-100 focus:!ring-4 focus:!ring-orange-50 disabled:!bg-zinc-50"
            buttonClass="!border-gray-100 !bg-white !rounded-l-[18px]"
            containerClass="w-full"
          />
        </div>
      </div>
    </div>
  );

  const renderPreferencesTab = () => (
    <div className="space-y-8">
      <div className="bg-white rounded-[32px] border border-gray-50 shadow-sm p-6">
        <h3 className={`${sectionTitleClass} mb-5`}>Notifications</h3>

        <div className="space-y-3">
          {[
            { id: "email", label: "Email notifications" },
            { id: "push", label: "Push notifications" },
            { id: "sms", label: "SMS notifications" },
          ].map((method) => (
            <label
              key={method.id}
              className="flex items-center justify-between gap-4 p-4 bg-[#F8F9FB] rounded-[20px] border border-gray-50"
            >
              <span className="text-[11px] font-black uppercase tracking-widest text-[#0A1128]">
                {method.label}
              </span>

              <input
                type="checkbox"
                checked={profileData.notificationMethods.includes(method.id)}
                onChange={(e) => {
                  const methods = profileData.notificationMethods;

                  if (e.target.checked) {
                    handleInputChange("notificationMethods", [
                      ...methods,
                      method.id,
                    ]);
                  } else {
                    handleInputChange(
                      "notificationMethods",
                      methods.filter((m) => m !== method.id),
                    );
                  }
                }}
                disabled={!isEditing}
                className="h-4 w-4 text-[#FF5C35] focus:ring-[#FF5C35] border-gray-300 rounded"
              />
            </label>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-[32px] border border-gray-50 shadow-sm p-6">
        <h3 className={`${sectionTitleClass} mb-5`}>Marketing</h3>

        <label className="flex items-center justify-between gap-4 p-4 bg-[#F8F9FB] rounded-[20px] border border-gray-50">
          <span className="text-[11px] font-black uppercase tracking-widest text-[#0A1128]">
            Receive marketing materials and special offers
          </span>

          <input
            type="checkbox"
            checked={profileData.marketingOptIn}
            onChange={(e) =>
              handleInputChange("marketingOptIn", e.target.checked)
            }
            disabled={!isEditing}
            className="h-4 w-4 text-[#FF5C35] focus:ring-[#FF5C35] border-gray-300 rounded"
          />
        </label>
      </div>
    </div>
  );

  const renderPetsTab = () => {
    const selectedPet = pets.find(
      (pet) => String(pet.id) === String(selectedPetId),
    );

    const isPetEditing =
      selectedPet && String(editingPetId) === String(selectedPet.id);

    const closePetModal = () => {
      setSelectedPetId(null);
      setEditingPetId(null);
    };

    const openPetDetails = (petId) => {
      setSelectedPetId(petId);
      setEditingPetId(null);
    };

    const renderPetInfoItem = (label, value) => (
      <div className="bg-[#F8F9FB] rounded-[20px] border border-gray-50 p-4">
        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">
          {label}
        </p>
        <p className="text-[12px] font-black text-[#0A1128] uppercase tracking-tight break-words">
          {value || "—"}
        </p>
      </div>
    );

    const renderPetForm = (pet) => (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className={labelClass}>Name</label>
            <input
              type="text"
              value={pet.name}
              onChange={(e) => updatePet(pet.id, { name: e.target.value })}
              className={fieldClass}
              placeholder="Pet name"
            />
          </div>

          <div>
            <label className={labelClass}>Species</label>
            <select
              value={pet.species}
              onChange={(e) => updatePet(pet.id, { species: e.target.value })}
              className={fieldClass}
            >
              <option value="">Select species</option>
              <option value="dog">Dog</option>
              <option value="cat">Cat</option>
              <option value="bird">Bird</option>
              <option value="rabbit">Rabbit</option>
              <option value="fish">Fish</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className={labelClass}>Breed</label>
            <input
              type="text"
              value={pet.breed}
              onChange={(e) => updatePet(pet.id, { breed: e.target.value })}
              className={fieldClass}
              placeholder="Breed"
            />
          </div>

          <div>
            <label className={labelClass}>Gender</label>
            <select
              value={pet.gender}
              onChange={(e) => updatePet(pet.id, { gender: e.target.value })}
              className={fieldClass}
            >
              <option value="">Select gender</option>
              <option value="unknown">Unknown</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>

          <div>
            <label className={labelClass}>
              Date of Birth / Age:{" "}
              {pet.dateOfBirth ? calculateAge(pet.dateOfBirth) : 0}
            </label>
            <input
              type="date"
              value={pet.dateOfBirth}
              onChange={(e) =>
                updatePet(pet.id, { dateOfBirth: e.target.value })
              }
              className={fieldClass}
            />
          </div>

          <div>
            <label className={labelClass}>Weight (kg)</label>
            <input
              type="number"
              step="0.1"
              value={pet.weight}
              onChange={(e) =>
                updatePet(pet.id, {
                  weight:
                    e.target.value === "" ? "" : parseFloat(e.target.value),
                })
              }
              className={fieldClass}
              placeholder="0.0"
            />
          </div>

          <div>
            <label className={labelClass}>Microchip Number</label>
            <input
              type="text"
              value={pet.microchipId}
              onChange={(e) =>
                updatePet(pet.id, { microchipId: e.target.value })
              }
              className={fieldClass}
              placeholder="ABC123456789"
            />
          </div>

          <div className="flex items-end">
            <label className="w-full flex items-center justify-between gap-4 p-4 bg-[#F8F9FB] rounded-[20px] border border-gray-50">
              <span className="text-[10px] font-black uppercase tracking-widest text-[#0A1128]">
                Sterilized/Neutered
              </span>

              <input
                type="checkbox"
                checked={pet.sterilized}
                onChange={(e) =>
                  updatePet(pet.id, { sterilized: e.target.checked })
                }
                className="h-4 w-4 text-[#FF5C35] focus:ring-[#FF5C35] border-gray-300 rounded"
              />
            </label>
          </div>
        </div>

        <div>
          <label className={labelClass}>Pet Photo</label>

          <div className="flex flex-col md:flex-row items-start gap-5 bg-[#F8F9FB] rounded-[28px] border border-gray-50 p-5">
            <div className="w-24 h-24 bg-white rounded-[24px] flex items-center justify-center overflow-hidden shadow-sm border border-gray-50">
              {pet.photoUrl ? (
                <img
                  src={pet.photoUrl}
                  alt={pet.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <HeartIcon className="w-9 h-9 text-slate-300" />
              )}
            </div>

            <div className="flex-1 w-full">
              <FileUpload
                onFilesSelect={(files) => handlePetPhotoUpload(pet.id, files)}
                maxFiles={1}
                maxFileSize={5 * 1024 * 1024}
                acceptedTypes={["image/jpeg", "image/png", "image/webp"]}
                allowMultiple={false}
                showPreview={false}
                className="max-w-sm"
              >
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-[#0A1128]">
                    Upload pet photo
                  </p>
                  <p className="text-[9px] font-bold text-slate-400 mt-1 uppercase">
                    JPG, PNG or WebP up to 5MB
                  </p>
                </div>
              </FileUpload>
            </div>
          </div>
        </div>

        <div className="space-y-5">
          <div>
            <label className={labelClass}>Allergies</label>
            <input
              type="text"
              value={pet.allergies.join(", ")}
              onChange={(e) =>
                updatePet(pet.id, {
                  allergies: e.target.value
                    .split(",")
                    .map((item) => item.trim())
                    .filter(Boolean),
                })
              }
              className={fieldClass}
              placeholder="chicken, beef"
            />
          </div>

          <div>
            <label className={labelClass}>Medications and Dosages</label>
            <textarea
              value={pet.medications}
              onChange={(e) =>
                updatePet(pet.id, { medications: e.target.value })
              }
              className={fieldClass}
              rows={2}
              placeholder="Vitamins for coat - 1 tablet per day"
            />
          </div>

          <div>
            <label className={labelClass}>Special Needs</label>
            <textarea
              value={pet.specialNeeds}
              onChange={(e) =>
                updatePet(pet.id, { specialNeeds: e.target.value })
              }
              className={fieldClass}
              rows={2}
              placeholder="Dietary food, specific behavior"
            />
          </div>

          <div>
            <label className={labelClass}>Notes</label>
            <textarea
              value={pet.notes}
              onChange={(e) => updatePet(pet.id, { notes: e.target.value })}
              className={fieldClass}
              rows={3}
              placeholder="Additional information about the pet"
            />
          </div>
        </div>
      </div>
    );

    const renderPetDetails = (pet) => (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {renderPetInfoItem("Name", pet.name)}
          {renderPetInfoItem("Species", pet.species)}
          {renderPetInfoItem("Breed", pet.breed)}
          {renderPetInfoItem("Gender", pet.gender)}
          {renderPetInfoItem(
            "Age",
            pet.dateOfBirth ? `${calculateAge(pet.dateOfBirth)} years` : "—",
          )}
          {renderPetInfoItem("Weight", pet.weight ? `${pet.weight} kg` : "—")}
          {renderPetInfoItem("Microchip", pet.microchipId)}
          {renderPetInfoItem("Sterilized", pet.sterilized ? "Yes" : "No")}
          {renderPetInfoItem(
            "Allergies",
            pet.allergies?.length ? pet.allergies.join(", ") : "—",
          )}
        </div>

        <div className="grid grid-cols-1 gap-4">
          {renderPetInfoItem("Medications", pet.medications)}
          {renderPetInfoItem("Special Needs", pet.specialNeeds)}
          {renderPetInfoItem("Notes", pet.notes)}
        </div>
      </div>
    );

    return (
      <div className="space-y-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4">
          <div>
            <h3 className={sectionTitleClass}>My Pets</h3>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-300 mt-1">
              Compact pet cards with details in popup
            </p>
          </div>

          <button
            type="button"
            onClick={addPet}
            className="flex items-center justify-center gap-2 bg-zinc-900 text-white rounded-[18px] px-5 py-3 text-[10px] font-black uppercase tracking-widest shadow-lg shadow-zinc-200 active:scale-95 transition-all hover:bg-[#0A1128]"
          >
            <PlusIcon className="w-4 h-4" />
            <span>Add Pet</span>
          </button>
        </div>

        {pets.length === 0 ? (
          <div className="rounded-[32px] border border-dashed border-gray-200 bg-[#F8F9FB] p-10 text-center">
            <HeartIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
              No pets yet. Click Add Pet to create one.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {pets.map((pet) => (
              <div
                key={pet.id}
                className="group bg-white rounded-[32px] border border-gray-50 p-5 shadow-sm hover:shadow-md transition-all duration-300"
              >
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 bg-[#F8F9FB] rounded-[24px] flex items-center justify-center overflow-hidden border border-gray-50 shadow-sm shrink-0">
                    {pet.photoUrl ? (
                      <img
                        src={pet.photoUrl}
                        alt={pet.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <HeartIcon className="w-9 h-9 text-[#FF5C35]" />
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <h4 className="text-lg font-black text-[#0A1128] italic tracking-tight truncate">
                      {pet.name || "New Pet"}
                    </h4>

                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-300 mt-1 truncate">
                      {pet.species || "No species"}{" "}
                      {pet.breed ? `• ${pet.breed}` : ""}
                    </p>

                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-300 mt-2">
                      {pet.dateOfBirth
                        ? `${calculateAge(pet.dateOfBirth)} years`
                        : "Age unknown"}
                    </p>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => openPetDetails(pet.id)}
                    className="rounded-[16px] bg-zinc-900 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-white shadow-lg shadow-zinc-200 hover:bg-[#0A1128] active:scale-95 transition-all"
                  >
                    Details
                  </button>

                  <button
                    type="button"
                    onClick={() => deletePet(pet.id)}
                    className="rounded-[16px] bg-red-50 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-red-500 hover:bg-red-100 active:scale-95 transition-all"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {selectedPet && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4 py-6">
            <button
              type="button"
              aria-label="Close pet modal"
              onClick={closePetModal}
              className="absolute inset-0 bg-[#0A1128]/50 backdrop-blur-sm"
            />

            <div className="relative z-10 w-full max-w-5xl max-h-[90vh] overflow-hidden bg-white rounded-[40px] border border-gray-50 shadow-2xl">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5 px-6 sm:px-8 py-6 border-b border-gray-50 bg-white">
                <div className="flex items-center gap-5 min-w-0">
                  <div className="w-20 h-20 bg-[#F8F9FB] rounded-[24px] flex items-center justify-center overflow-hidden border border-gray-50 shadow-sm shrink-0">
                    {selectedPet.photoUrl ? (
                      <img
                        src={selectedPet.photoUrl}
                        alt={selectedPet.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <HeartIcon className="w-9 h-9 text-[#FF5C35]" />
                    )}
                  </div>

                  <div className="min-w-0">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#FF5C35] mb-2">
                      Pet Profile
                    </p>

                    <h3 className="text-3xl font-black text-[#0A1128] uppercase italic tracking-tighter truncate">
                      {selectedPet.name || "New Pet"}
                    </h3>

                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-300 mt-2">
                      {isPetEditing ? "Editing mode" : "Details mode"}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 shrink-0">
                  {!isPetEditing && (
                    <button
                      type="button"
                      onClick={() => setEditingPetId(selectedPet.id)}
                      className="flex items-center gap-2 rounded-[18px] bg-zinc-900 px-5 py-3 text-[10px] font-black uppercase tracking-widest text-white shadow-lg shadow-zinc-200 hover:bg-[#0A1128] active:scale-95 transition-all"
                    >
                      <PencilIcon className="w-4 h-4" />
                      <span>Edit</span>
                    </button>
                  )}

                  {isPetEditing && (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingPetId(null);
                        }}
                        className="rounded-[18px] bg-white border border-gray-100 px-5 py-3 text-[10px] font-black uppercase tracking-widest text-[#0A1128] shadow-sm hover:bg-zinc-50 active:scale-95 transition-all"
                      >
                        Cancel
                      </button>

                      <button
                        type="button"
                        onClick={async () => {
                          const saved = await savePet(selectedPet);

                          if (saved) {
                            setEditingPetId(null);
                            setSelectedPetId(null);
                          }
                        }}
                        disabled={savingPetId === selectedPet.id}
                        className="flex items-center gap-2 rounded-[18px] bg-zinc-900 px-5 py-3 text-[10px] font-black uppercase tracking-widest text-white shadow-lg shadow-zinc-200 hover:bg-[#0A1128] active:scale-95 transition-all disabled:opacity-50 disabled:bg-zinc-300"
                      >
                        <CheckIcon className="w-4 h-4" />
                        <span>
                          {savingPetId === selectedPet.id ? "Saving..." : "Save"}
                        </span>
                      </button>
                    </>
                  )}

                  <button
                    type="button"
                    onClick={async () => {
                      const deleted = await deletePet(selectedPet.id);

                      if (deleted) {
                        closePetModal();
                      }
                    }}
                    className="rounded-[18px] bg-red-50 px-5 py-3 text-[10px] font-black uppercase tracking-widest text-red-500 hover:bg-red-100 active:scale-95 transition-all"
                  >
                    Delete
                  </button>

                  <button
                    type="button"
                    onClick={closePetModal}
                    className="rounded-[18px] bg-orange-50 px-5 py-3 text-[10px] font-black uppercase tracking-widest text-[#FF5C35] hover:bg-orange-100 active:scale-95 transition-all"
                  >
                    Close
                  </button>
                </div>
              </div>

              <div className="max-h-[calc(90vh-132px)] overflow-y-auto p-6 sm:p-8 bg-[#F8F9FB] custom-scrollbar">
                <div className="bg-white rounded-[32px] border border-gray-50 p-5 sm:p-8 shadow-sm">
                  {isPetEditing
                    ? renderPetForm(selectedPet)
                    : renderPetDetails(selectedPet)}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderActiveTab = () => {
    switch (activeTab) {
      case "personal":
        return renderPersonalTab();
      case "contact":
        return renderContactTab();
      case "emergency":
        return renderEmergencyTab();
      case "preferences":
        return renderPreferencesTab();
      case "pets":
        return renderPetsTab();
      default:
        return renderPersonalTab();
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FB] text-zinc-900 font-sans py-8">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#FF5C35] mb-3">
              Account Settings
            </p>

            <h1 className="text-4xl md:text-5xl font-black text-[#0A1128] uppercase italic tracking-tighter">
              User <span className="text-slate-300">Profile</span>
            </h1>

            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mt-3">
              Manage your personal information and pets
            </p>
          </div>

          <button
            type="button"
            onClick={() => setIsEditing(!isEditing)}
            className={`flex items-center justify-center gap-2 rounded-[18px] px-6 py-3 text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 ${
              isEditing
                ? "bg-white text-[#0A1128] border border-gray-100 shadow-sm hover:bg-zinc-50"
                : "bg-zinc-900 text-white shadow-lg shadow-zinc-200 hover:bg-[#0A1128]"
            }`}
          >
            <PencilIcon className="w-4 h-4" />
            <span>{isEditing ? "Cancel" : "Edit"}</span>
          </button>
        </div>

        <div className="bg-white border border-gray-50 rounded-[40px] overflow-hidden shadow-sm">
          <div className="border-b border-gray-50 bg-white px-4 sm:px-6">
            <nav className="flex gap-2 overflow-x-auto py-4 custom-scrollbar">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;

                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 whitespace-nowrap rounded-[18px] px-5 py-3 text-[10px] font-black uppercase tracking-widest transition-all ${
                      isActive
                        ? "bg-zinc-900 text-white shadow-lg shadow-zinc-200 scale-[1.02]"
                        : "bg-[#F8F9FB] text-slate-400 hover:bg-white hover:text-[#0A1128] hover:shadow-sm"
                    }`}
                  >
                    <Icon
                      className={`w-4 h-4 ${
                        isActive ? "text-[#FF5C35]" : "text-slate-300"
                      }`}
                    />
                    <span>{tab.name}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="p-5 sm:p-8 lg:p-10">{renderActiveTab()}</div>

          {isEditing && activeTab !== "pets" && (
            <div className="border-t border-gray-50 px-5 sm:px-8 lg:px-10 py-5 bg-[#F8F9FB]">
              <div className="flex flex-col sm:flex-row justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="rounded-[18px] px-6 py-3 bg-white border border-gray-100 text-[#0A1128] text-[10px] font-black uppercase tracking-widest shadow-sm hover:bg-zinc-50 active:scale-95 transition-all"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    showSuccess("Изменения профиля сохранены", "Сохранено");
                  }}
                  className="rounded-[18px] px-6 py-3 bg-zinc-900 text-white text-[10px] font-black uppercase tracking-widest shadow-lg shadow-zinc-200 hover:bg-[#0A1128] active:scale-95 transition-all"
                >
                  Save Changes
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;